import axios from 'axios';
import { io } from 'socket.io-client';

const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production'
    ? 'https://api.lotwleague.com/api'  // Production API URL
    : process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_NETWORK_API_URL || 'http://localhost:3001/api',
  withCredentials: true
});

// State for token refresh
let isRefreshing = false;
let refreshSubscribers = [];

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const token = await refreshToken();
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Authentication endpoints
api.login = async (credentials) => {
  const response = await api.post('/users/login', credentials);
  if (response.data.token) {
    localStorage.setItem('token', response.data.token);
    disconnectSocket();
    initSocket();
  }
  return response;
};
api.register = (userData) => api.post('/users/register', userData);

// User profile and settings
api.getProfile = () => api.get('/users/profile');
api.updateSettings = (settings) => api.put('/users/settings', settings);
api.resetPassword = (newPassword) => api.put('/users/reset-password', { password: newPassword });
api.getByeStatus = () => api.get('/users/bye-status');

// Calendar and games
api.getCalendarStatus = () => api.get('/calendar/status');
api.fetchGames = (week) => api.get(`/games?week=${week}`);
api.fetchCalendar = () => api.get('/calendar');
api.fetchCurrentWeek = () => api.get('/calendar/current-week');

// Picks and locks
api.fetchLocksForWeek = (week) => api.get(`/picks/week/${week}/all`);
api.updateMaxPicks = (season, week, maxPicks) =>
    api.put('/calendar/update-max-picks', { season, week, maxPicks });
api.fetchUserSeasonLocks = () => api.get('/picks/season-locks');

// Calendar management
api.createCustomWeek = (season, week, startDate, endDate, notes, maxPicks) =>
    api.post('/calendar/create-week', { season, week, startDate, endDate, notes, maxPicks });
api.deleteCustomWeek = (season, week) =>
    api.delete('/calendar/delete-week', { data: { season, week } });

// User statistics
api.getActiveUserCount = () => api.get('/users/active-count');

// Socket management
let socket;

export const initSocket = () => {
  if (!socket) {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token available for socket connection');
      return null;
    }

    const socketUrl = process.env.NODE_ENV === 'production'
      ? 'https://api.lotwleague.com'  // Production socket URL
      : process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';

    socket = io(socketUrl, {
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: false,
      auth: { token }
    });

    socket.on('connect', () => {
      console.log('Socket connected successfully');
    });

    socket.on('connect_error', async (error) => {
      console.error('Socket connection error:', error);
      if (error.message === 'Authentication error: Invalid token') {
        try {
          const newToken = await refreshToken();
          socket.auth.token = newToken;
          socket.connect();
        } catch (refreshError) {
          console.error('Failed to refresh token for socket connection:', refreshError);
          disconnectSocket();
          if (!window.location.pathname.includes('/login')) {
            window.dispatchEvent(new CustomEvent('unauthorized'));
          }
        }
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      if (reason === 'io server disconnect') {
        disconnectSocket();
        if (!window.location.pathname.includes('/login')) {
          window.dispatchEvent(new CustomEvent('unauthorized'));
        }
      }
    });

    socket.connect();
  }
  return socket;
};

export const getSocket = () => socket;

export const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      refreshSubscribers.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      throw new Error('No token found');
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || 'http://192.168.86.41:3001/api'}/users/refresh-token`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        },
        withCredentials: true
      }
    );

    if (!response.data || !response.data.token) {
      throw new Error('Invalid response from refresh token endpoint');
    }

    const { token } = response.data;
    localStorage.setItem('token', token);

    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];

    return token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    refreshSubscribers = [];
    localStorage.removeItem('token');

    if (!window.location.pathname.includes('/login')) {
      window.dispatchEvent(new CustomEvent('unauthorized'));
    }

    throw error;
  } finally {
    isRefreshing = false;
  }
};

export const disconnectSocket = () => {
  if (socket) {
    console.log('Disconnecting socket');
    socket.disconnect();
    socket = null;
  }
};

// Leaderboard endpoints
api.getLeaderboard = () => api.get('/leaderboard');
api.getLeaderboardOverview = () => api.get('/leaderboard/overview');
api.getLeagueMetrics = () => api.get('/leaderboard/metrics');

// Add version endpoint
api.getServerVersion = () => api.get('/system/version');

export default api;


